import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AuthUser } from '../lib/apiClient';
import { primaryRoleLabel } from '../utils/roles';

interface AccountMenuProps {
  user: AuthUser | null;
  onLogout: () => void;
}

export default function AccountMenu({ user, onLogout }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="account-menu" ref={rootRef}>
      <button
        type="button"
        className="account-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {user?.photo_url ? (
          <img src={user.photo_url} alt="" className="avatar avatar-photo" />
        ) : (
          <div className="avatar">{user?.full_name?.charAt(0) ?? '?'}</div>
        )}
        <div className="account-text">
          <strong>{user?.full_name}</strong>
          <small>{primaryRoleLabel(user?.roles)}</small>
        </div>
      </button>
      {open && (
        <div className="account-dropdown">
          <Link
            className="account-dropdown-item account-dropdown-item-neutral"
            to="/account"
            onClick={() => setOpen(false)}
          >
            Account
          </Link>
          <Link
            className="account-dropdown-item account-dropdown-item-neutral"
            to="/help"
            onClick={() => setOpen(false)}
          >
            Help
          </Link>
          <Link
            className="account-dropdown-item account-dropdown-item-neutral"
            to="/support"
            onClick={() => setOpen(false)}
          >
            Support
          </Link>
          <div className="account-dropdown-divider" />
          <button
            type="button"
            className="account-dropdown-item"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
